import { useMap } from "@vis.gl/react-google-maps";
import { useEffect } from "react";

import { computeGraceM } from "@/pages/Buscar/useSearchState";

// Debug-only companion to BoundaryOverlay. Per circle-origin polygon, paints
// reference layers on the map:
//
//   raw     (gray dashed    — Google's pre-clamp/cap half-diagonal of
//            ring)            place.geometry.viewport. ONLY present for live
//                             autocomplete picks (TopBar forwards rawRadiusM
//                             through applyPlaceBoundary); cold-load
//                             hydration from `?radio=` only round-trips the
//                             clamped value. Dotted so when raw == rendered
//                             (no clamp/cap fired) the ring stays visible
//                             on top of the navy filled visual disc instead
//                             of being absorbed by it.
//   filter  (orange dashed   — `rendered + computeGraceM(p)`, the spatial
//            ring)            radius apiBody sends to the backend. The
//                             cluster + marker count on the map should be ≤
//                             what falls inside this ring. Dotted for visual
//                             consistency with the raw ring.
//   padding (purple bands)  — Two filled regions on the binding axis (the
//                             shorter dimension of the current map div),
//                             one above and one below the filter circle
//                             (landscape) or left and right (portrait):
//                             inner edge tangent to the filter circle,
//                             outer edge offset by `paddingPx`-in-meters
//                             at the auto-fit zoom. This is the only axis
//                             where CIRCLE_VIEWPORT_PADDING_PX is a real
//                             geometric constraint — fitMapToCircle picks
//                             zoom so 2*filterR fits in (min_axis − 2*pad)
//                             pixels, so the band's outer edge sits flush
//                             against the viewport edge on the binding axis
//                             at the auto-fit moment. On the wider axis
//                             nothing is "padded" — the leftover is just
//                             aspect-ratio slack.
//
// The strict visual (navy) is still painted by BoundaryOverlay, untouched.
//
// Gated upstream by BuscarLayout's debugVisible. Never shipped — this
// component exists only for in-branch geometry verification.

const RAW_STROKE = "#9CA3AF";
const FILTER_STROKE = "#F97316";
const PADDING_STROKE = "#7C3AED";

// Half-extent of each padding band in the non-binding direction (meters).
// Generously wide — at any reasonable zoom the band spans the full map;
// off-screen tail is invisible.
const BAND_HALF_LENGTH_M = 150_000;

const offsetLat = (m) => m / 111_000;
const offsetLng = (m, lat) => m / (111_000 * Math.cos((lat * Math.PI) / 180));

const DebugBoundaryOverlay = ({ polygon, paddingPx = 80 }) => {
    const map = useMap();

    useEffect(() => {
        if (!map || !polygon) return;
        if (typeof window === "undefined" || !window.google?.maps) return;
        if (polygon.shape !== "circle") return;

        const { coords } = polygon;
        const center = coords?.center;
        const rendered = coords?.radius;
        if (!center || !Number.isFinite(rendered)) return;

        const overlays = [];

        const grace = computeGraceM(polygon);
        const filterR = Number.isFinite(grace) && grace > 0 ? rendered + grace : rendered;

        overlays.push(buildDashedRing(map, center, filterR, FILTER_STROKE, 3, 60));

        // Padding pair on the binding axis. fitMapToCircle picks zoom so
        // 2*filterR meters fit in (min(w, h) − 2*paddingPx) pixels — so
        // paddingPx in meters at that zoom = paddingPx * 2*filterR / padded.
        const div = map.getDiv?.();
        const w = div?.clientWidth ?? 0;
        const h = div?.clientHeight ?? 0;
        const padded = Math.min(w, h) - 2 * paddingPx;
        if (padded > 0 && filterR > 0) {
            const paddingM = (paddingPx * 2 * filterR) / padded;
            // Landscape: binding axis is height → bands run east-west, inner
            // edge at center.lat ± (filterR / 111000), outer edge offset
            // outward by paddingM. Portrait: binding axis is width →
            // analogous on the lng axis.
            const landscape = w >= h;
            if (landscape) {
                const dLatFilter = offsetLat(filterR);
                const dLatPadding = offsetLat(paddingM);
                const dLngSpan = offsetLng(BAND_HALF_LENGTH_M, center.lat);
                for (const sign of [1, -1]) {
                    const inner = center.lat + sign * dLatFilter;
                    const outer = center.lat + sign * (dLatFilter + dLatPadding);
                    overlays.push(
                        buildBand(map, [
                            { lat: inner, lng: center.lng - dLngSpan },
                            { lat: inner, lng: center.lng + dLngSpan },
                            { lat: outer, lng: center.lng + dLngSpan },
                            { lat: outer, lng: center.lng - dLngSpan },
                        ])
                    );
                }
            } else {
                const dLngFilter = offsetLng(filterR, center.lat);
                const dLngPadding = offsetLng(paddingM, center.lat);
                const dLatSpan = offsetLat(BAND_HALF_LENGTH_M);
                for (const sign of [1, -1]) {
                    const inner = center.lng + sign * dLngFilter;
                    const outer = center.lng + sign * (dLngFilter + dLngPadding);
                    overlays.push(
                        buildBand(map, [
                            { lat: center.lat - dLatSpan, lng: inner },
                            { lat: center.lat + dLatSpan, lng: inner },
                            { lat: center.lat + dLatSpan, lng: outer },
                            { lat: center.lat - dLatSpan, lng: outer },
                        ])
                    );
                }
            }
        }

        const raw = coords?.rawRadius;
        // Always draw raw when available — when it coincides with rendered
        // (no clamp/cap fired), the gray ring sits exactly on the navy edge,
        // which is itself informative. Dedup-by-magnitude would silently hide
        // the layer for non-sublocality places that pass the clamp untouched
        // (e.g. Guadalajara at ~10km falls inside [1km, 30km] → raw ==
        // rendered) and you'd be unable to tell apart "no viewport" from
        // "viewport unmodified."
        if (Number.isFinite(raw) && raw > 0) {
            overlays.push(buildDashedRing(map, center, raw, RAW_STROKE, 2, 55));
        }

        return () => {
            overlays.forEach((o) => o.setMap(null));
        };
    }, [map, polygon, paddingPx]);

    return null;
};

const buildBand = (map, paths) =>
    new window.google.maps.Polygon({
        paths,
        strokeColor: PADDING_STROKE,
        strokeOpacity: 0.9,
        strokeWeight: 1,
        fillColor: PADDING_STROKE,
        fillOpacity: 0.18,
        clickable: false,
        zIndex: 50,
        map,
    });

// Dashed ring via Polyline + icon-symbol trick (Google Maps doesn't expose
// dashed strokes on Circle). The line itself is invisible (strokeOpacity:
// 0); each `icon` is a short line segment ("M 0,-1 0,1" — a unit-length
// vertical stroke in symbol space) which Google Maps auto-rotates
// perpendicular to the path, producing a tangent-aligned dash. Spacing
// tuned so dashes read clearly at typical neighborhood zooms.
const buildDashedRing = (map, center, radiusM, color, strokeWeight, zIndex) => {
    const points = sampleCircle(center, radiusM, 128);
    return new window.google.maps.Polyline({
        path: points,
        geodesic: false,
        strokeOpacity: 0,
        icons: [
            {
                icon: {
                    path: "M 0,-1 0,1",
                    strokeColor: color,
                    strokeOpacity: 1,
                    strokeWeight,
                    scale: 4,
                },
                offset: "0",
                repeat: "14px",
            },
        ],
        clickable: false,
        zIndex,
        map,
    });
};

const sampleCircle = (center, radiusM, n) => {
    const points = [];
    const cosLat = Math.cos((center.lat * Math.PI) / 180);
    const dLatPerM = 1 / 111_000;
    const dLngPerM = 1 / (111_000 * cosLat);
    for (let i = 0; i < n; i++) {
        const theta = (i / n) * 2 * Math.PI;
        points.push({
            lat: center.lat + Math.sin(theta) * radiusM * dLatPerM,
            lng: center.lng + Math.cos(theta) * radiusM * dLngPerM,
        });
    }
    points.push(points[0]);
    return points;
};

export default DebugBoundaryOverlay;
